import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateRight,
  faTriangleExclamation,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";

export default function AiRerunConfirmDialog({
  open,
  editedCount = 0,
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  const plural = editedCount === 1 ? "" : "s";

  /* PORTALLED TO THE BODY ON PURPOSE. This dialog is mounted deep inside the
     quote workspace, under a framer-motion wrapper — and a transformed ancestor
     becomes the containing block for position:fixed. `fixed inset-0` therefore
     sized itself to the content panel: the sidebar and header stayed bright and
     undimmed beside a blurred page, which reads as a half-broken modal. Same
     trap as MomentumScroll's transform, and the same fix the cost table's
     fullscreen modal already uses.

     z-[100002] because a blocking confirm that things float over is not
     blocking anything: the copilot FAB is z-10000, its side sheet z-10001 and
     the Notes button z-1000, and at z-200 all three sat on top of it. */
  const dialog = (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100002] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ai-rerun-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onCancel?.();
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-violet-200/90 bg-white shadow-[0_30px_70px_-20px_rgba(124,58,237,0.45)] dark:border-violet-500/25 dark:bg-[oklch(0.225_0.026_264)]"
          >
            {/* Animated rotating conic gradient ring */}
            <motion.div
              aria-hidden
              className="pointer-events-none absolute -inset-px rounded-[1.05rem] opacity-25"
              style={{
                background:
                  "conic-gradient(from 0deg, #a78bfa, #60a5fa, #818cf8, #c4b5fd, #a78bfa, #60a5fa, #a78bfa)",
              }}
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
            />

            <div className="relative rounded-2xl bg-white dark:bg-[oklch(0.225_0.026_264)]">
              {/* Top shimmer bar */}
              <div className="relative h-[3px] w-full overflow-hidden bg-violet-100 dark:bg-violet-950/60">
                <motion.div
                  className="absolute inset-y-0 w-1/3 bg-[linear-gradient(90deg,transparent,#8b5cf6_30%,#6366f1_50%,#3b82f6_70%,transparent)]"
                  animate={{ x: ["-120%", "320%"] }}
                  transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                />
              </div>

              {/* Header band */}
              <div className="relative bg-gradient-to-br from-violet-50/90 via-white to-indigo-50/60 px-5 pb-3 pt-5 dark:from-violet-950/50 dark:via-slate-900 dark:to-indigo-950/35 sm:px-6">
                <div className="flex items-start gap-3">
                  {/* AI icon tile with pulsing glow */}
                  <div className="relative h-11 w-11 shrink-0">
                    <motion.div
                      className="absolute inset-0 rounded-xl bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_50%,#3b82f6_100%)]"
                      animate={{
                        boxShadow: [
                          "0 8px 22px -6px rgba(124,58,237,0.55)",
                          "0 12px 30px -6px rgba(124,58,237,0.85)",
                          "0 8px 22px -6px rgba(124,58,237,0.55)",
                        ],
                      }}
                      transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
                    />
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center text-white"
                      animate={{ rotate: [0, 8, -6, 0] }}
                      transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
                    >
                      <FontAwesomeIcon
                        icon={faWandMagicSparkles}
                        className="text-[18px] drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]"
                      />
                    </motion.div>
                    {/* Tiny rotating sparkle dot */}
                    <motion.span
                      aria-hidden
                      className="pointer-events-none absolute left-1/2 top-1/2 h-[8px] w-[8px]"
                      style={{ marginLeft: "-4px", marginTop: "-4px" }}
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 5, ease: "linear" }}
                    >
                      <span className="absolute -top-2 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-violet-300 shadow-[0_0_6px_rgba(167,139,250,0.85)]" />
                    </motion.span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2
                      id="ai-rerun-title"
                      className="text-[15px] font-semibold tracking-tight text-slate-900 dark:text-slate-100"
                    >
                      Are you sure?
                    </h2>
                    <p className="mt-0.5 text-[11.5px] font-medium uppercase tracking-[0.14em] text-violet-600/90 dark:text-violet-300/90">
                      This will re-do the AI suggestions
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-5 pb-5 pt-1 sm:px-6 sm:pb-6">
                {/* Edits-warning callout */}
                {editedCount > 0 ? (
                  <div className="flex items-start gap-2.5 rounded-xl border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 dark:border-amber-500/30 dark:bg-amber-950/30">
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      className="mt-0.5 text-[12px] text-amber-600 dark:text-amber-400"
                    />
                    <p className="text-[12.5px] leading-relaxed text-amber-900 dark:text-amber-100">
                      You&apos;ve edited{" "}
                      <span className="font-semibold">
                        {editedCount} field{plural}
                      </span>{" "}
                      since the last AI parse.
                    </p>
                  </div>
                ) : null}

                <p className="mt-3 text-[13px] leading-relaxed text-slate-600 dark:text-slate-400">
                  Re-parsing will produce a fresh set of AI suggestions and replace the current ones.
                  Empty fields get refilled; your manual edits are kept untouched.
                </p>

                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => onCancel?.()}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.3_0.024_264)]"
                  >
                    No, keep current
                  </button>
                  <button
                    type="button"
                    onClick={() => onConfirm?.()}
                    className="group relative inline-flex items-center gap-1.5 overflow-hidden rounded-xl bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_45%,#4f46e5_100%)] px-4 py-2 text-[13px] font-semibold text-white shadow-[0_8px_22px_-8px_rgba(124,58,237,0.65)] transition-all hover:shadow-[0_12px_28px_-8px_rgba(124,58,237,0.85)] hover:brightness-110"
                  >
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] transition-transform duration-700 group-hover:translate-x-full"
                    />
                    <FontAwesomeIcon
                      icon={faRotateRight}
                      className="relative text-[11px] transition group-hover:rotate-180"
                    />
                    <span className="relative">Yes, re-parse</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Guarded for any non-DOM render path; the dialog is decoration-free enough
  // that returning it inline is a safe fallback.
  return typeof document !== "undefined"
    ? createPortal(dialog, document.body)
    : dialog;
}
